import { observer } from 'mobx-react-lite';
import * as React from 'react';
import PromptTemplateStore from '../stores/PromptTemplatesStore';
import { Button, Layout, Popconfirm, Space, Table, Tag, Tooltip } from 'antd';
import LayoutHeader from '../../../components/LayoutHeader';
import { CopyOutlined, PlusOutlined } from '@ant-design/icons';
import { Content } from 'antd/lib/layout/layout';
import { Utils } from '../../common/services/Utils';
import { PromptTemplate, PromptTemplateHighlightColour } from '../types';
import PromptTemplateImportExportStore from '../stores/PromptTemplateImportExportStore';
import PromptTemplateUploader from './PromptTemplateUploader/PromptTemplateUploader';
import DuplicatePromptTemplateDialog from './DuplicateTemplateDialog/DuplicatePromptTemplateDialog';
import { OverflowTooltip } from '../../common/components';
import './PromptTemplateList.less';

type Props = {
    store: PromptTemplateStore;
    importExportStore: PromptTemplateImportExportStore;
};

const highlightRowClassNames: Record<PromptTemplateHighlightColour, string> = {
    [PromptTemplateHighlightColour.Blue]: 'prompt-template-highlighted-row-blue',
    [PromptTemplateHighlightColour.Orange]: 'prompt-template-highlighted-row-orange',
    [PromptTemplateHighlightColour.Gray]: 'prompt-template-highlighted-row-gray'
};

const highlightMarkerClassNames: Record<PromptTemplateHighlightColour, string> = {
    [PromptTemplateHighlightColour.Blue]: 'prompt-template-highlight-marker-blue',
    [PromptTemplateHighlightColour.Orange]: 'prompt-template-highlight-marker-orange',
    [PromptTemplateHighlightColour.Gray]: 'prompt-template-highlight-marker-gray'
};

const getHighlightRowClassName = (template: PromptTemplate) => {
    if (!template.highlightColour) {
        return 'prompt-template-row';
    }

    return `prompt-template-row ${highlightRowClassNames[template.highlightColour]}`;
};

const getHighlightMarkerClassName = (template: PromptTemplate) => {
    if (!template.highlightColour) {
        return 'prompt-template-highlight-marker';
    }

    return `prompt-template-highlight-marker ${highlightMarkerClassNames[template.highlightColour]}`;
};

const PromptTemplateList: React.FC<Props> = ({ store, importExportStore }) => {
    React.useEffect(() => {
        if (store.currentProject) {
            store.loadPromptTemplates();
        }
    }, [store, store.currentProject]);

    const handleDuplicate = (templateId: string) => {
        store.setTemplateToDuplicate(templateId);
    };

    const getVersionTag = (template: PromptTemplate) => {
        if (!template) {
            return null;
        }
        if (template.isDraft) {
            return <Tag color="orange">Active Draft (based on v{template.currentVersion})</Tag>;
        }
        return (
            <Tag key="active-version" color="green">
                Active v{template.currentVersion}
            </Tag>
        );
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (title: string) => <OverflowTooltip text={title} style={{ fontWeight: 500 }} />,
            sorter: (a: PromptTemplate, b: PromptTemplate) => a.title.localeCompare(b.title)
        },
        {
            title: 'Version',
            key: 'version',
            width: 175,
            render: (_: unknown, record: PromptTemplate) => getVersionTag(record)
        },
        {
            title: 'Structure',
            dataIndex: 'promptTemplateStructure',
            key: 'promptTemplateStructure',
            width: 90,
            render: (promptTemplateStructure: string) => (
                <span style={{ color: '#999' }}> {promptTemplateStructure || 'Merged'}</span>
            )
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 150,
            ellipsis: true,
            render: (description: string) => <OverflowTooltip text={description} maxWidth={200} />
        },
        {
            title: 'Updated By',
            dataIndex: 'updatedBy',
            key: 'updatedBy',
            width: 140,
            render: (updatedBy: string) => store.getUserNameById(updatedBy)
        },
        {
            title: 'Last Modified',
            dataIndex: 'updatedTime',
            key: 'updatedTime',
            width: 180,
            sorter: (a: PromptTemplate, b: PromptTemplate) => Utils.safeDateStringCompare(a.updatedTime, b.updatedTime),
            render: (date: string | null) =>
                date ? (
                    <span style={{ color: '#999', fontSize: '0.8rem' }}>{Utils.formatDateStringShort(date)}</span>
                ) : (
                    '-'
                )
        },
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            width: 200,
            render: (id: string) => <span style={{ color: '#999', fontSize: '0.75rem' }}>{id}</span>
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 210,
            render: (_: unknown, record: PromptTemplate) => (
                <div className="row-actions-wrapper">
                    <Space size="middle">
                        <Tooltip title="Switch row highlight colour">
                            <Button
                                type="link"
                                size="small"
                                className="prompt-template-highlight-button"
                                data-id="button-switch-prompt-template-highlight-colour"
                                icon={<span className={getHighlightMarkerClassName(record)} />}
                                onClick={() => store.switchPromptTemplateHighlightColour(record.id)}
                            />
                        </Tooltip>
                        <Tooltip title="Edit template">
                            <Button
                                type="link"
                                size="small"
                                data-id="button-edit-prompt-template"
                                icon={<i className="alpha-icon lg edit-icon" />}
                                onClick={() => store.navigateToEditTemplate(record.id!)}
                            />
                        </Tooltip>
                        <Tooltip title="Export template">
                            <Button
                                type="link"
                                size="small"
                                data-id="button-edit-prompt-template"
                                icon={<i className="alpha-icon xs arrow-up prompt-template-export-icon" />}
                                onClick={() => importExportStore.exportTemplate(record.id!)}
                            />
                        </Tooltip>
                        <Tooltip title="Duplicate template">
                            <Button
                                type="link"
                                size="small"
                                data-id="button-copy-prompt-template"
                                icon={
                                    <CopyOutlined
                                        className="prompt-template-duplicate-icon"
                                        style={{ color: '#9ba0ab' }}
                                    />
                                }
                                onClick={() => handleDuplicate(record.id!)}
                            />
                        </Tooltip>
                        <Popconfirm
                            title={`Are you sure you want to delete the template "${record.title}"?`}
                            onConfirm={() => store.deletePromptTemplate(record.id)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <Tooltip title="Delete template">
                                <Button
                                    type="link"
                                    size="small"
                                    danger
                                    data-id="button-edit-prompt-template"
                                    icon={<i className="alpha-icon lg delete-icon-red" />}
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                </div>
            )
        }
    ];

    return (
        <Layout style={{ height: '100%', background: 'white' }}>
            <LayoutHeader
                subtitle={Utils.getSubtitle(store.currentProject)}
                title="Prompt Templates"
                buttons={[
                    <PromptTemplateUploader key="import-prompt-template" importExportStore={importExportStore} />,

                    <Button
                        key="create-prompt-template"
                        data-id="button-create-data-crafter-template"
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={() => store.navigateToCreateTemplate()}
                    >
                        Create Template
                    </Button>
                ]}
            />
            <Layout className="prompt-templates-list">
                <Content style={{ maxHeight: 'calc(100vh - 70px)', overflowY: 'auto' }}>
                    <Table
                        data-id="prompt-templates-table"
                        className="alpha-portal-table"
                        dataSource={store.sortedPromptTemplates}
                        columns={columns}
                        loading={store.isLoadingTemplates}
                        rowKey="id"
                        rowClassName={getHighlightRowClassName}
                        pagination={false}
                        sticky
                        scroll={{ x: 1325 }}
                    />
                </Content>
            </Layout>
            <DuplicatePromptTemplateDialog store={store} />
        </Layout>
    );
};

export default observer(PromptTemplateList);
